import { useState, useEffect, useRef } from "react";
import { SparklesIcon } from "./icons";
import { trackEvent } from "../../utils/analytics";
import Step1Map from "./Step1Map";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const isoMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  return dateStr;
};

const loadingMessages = [
  "Discovering hidden gems in your destination...",
  "Mapping out the perfect routes for you...",
  "Finding the best local experiences...",
  "Crafting your day-by-day adventure...",
  "Adding those special touches to your trip...",
];

const Step4Form = ({
  formData,
  isAnalyzing,
  isValidating,
  analysisComplete,
  itinerary,
  error,
  saveError,
  onRetrySave,
  validationResult,
  isEditMode,
  onRegenerate,
  onProceedAnyway,
  onGoToStep,
}) => {
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [saveBannerDismissed, setSaveBannerDismissed] = useState(false);

  const completedTracked = useRef(false);
  const viewedTracked = useRef(false);

  const validationTracked = useRef(false);

  useEffect(() => {
    setSaveBannerDismissed(false);
  }, [saveError]);

  useEffect(() => {
    if (analysisComplete && itinerary && !completedTracked.current) {
      completedTracked.current = true;

      const destinations = (formData?.stops || []).map(s => s.name).filter(Boolean).join(', ');
      trackEvent("itinerary_generation_completed", {
        destinations: destinations || formData?.destination || '',
      });
    }
  }, [analysisComplete, itinerary]);

  useEffect(() => {
    if (error) {
      trackEvent("itinerary_generation_failed", {
        error_message: error,
      });
    }
  }, [error]);

  useEffect(() => {
    if (!validationResult?.valid || validationTracked.current) return;

    validationTracked.current = true;

    trackEvent("trip_validation_failed", {
      budget_issue:
        validationResult.errors?.some((e) => e.type === "budget") ?? false,
      weather_issue:
        validationResult.errors?.some((e) => e.type === "weather") ?? false,
    });
  }, [validationResult]);

  useEffect(() => {
    if (analysisComplete && itinerary && !viewedTracked.current) {
      viewedTracked.current = true;

      // trackEvent("itinerary_viewed");
    }
  }, [analysisComplete, itinerary]);

  useEffect(() => {
    if (isAnalyzing) {
      completedTracked.current = false;
      viewedTracked.current = false;
      validationTracked.current = false;
    }
  }, [isAnalyzing]);

  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isAnalyzing]);
  if (isValidating) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-6">
          <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Romaya is analyzing your trip...
        </h3>
        <p className="text-gray-500 text-center max-w-md">
          Checking weather patterns and ensuring everything fits your budget
          perfectly.
        </p>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-6">
          <div className="w-20 h-20 border-4 border-teal-200 rounded-full animate-spin border-t-teal-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl">✨</span>
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Romaya is crafting your journey...
        </h3>
        <p className="text-gray-500 text-center max-w-md mb-4">
          Creating a personalized itinerary filled with unforgettable
          experiences just for you.
        </p>
        <div className="flex items-center gap-2 text-teal-600 text-sm">
          <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          <span className="transition-opacity duration-300">
            {loadingMessages[loadingMessageIndex]}
          </span>
        </div>
      </div>
    );
  }

  if (validationResult && !validationResult.valid) {
    const budgetError = validationResult.errors?.find(
      (e) => e.type === "budget",
    );
    const weatherError = validationResult.errors?.find(
      (e) => e.type === "weather",
    );
    const noBudgetProvided =
      !formData.budget || parseFloat(formData.budget) === 0;

    if (
      noBudgetProvided &&
      budgetError?.details?.suggestedBudget &&
      !weatherError
    ) {
      return (
        <div className="space-y-6">
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
            <h4 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Your Estimated Budget</span>
            </h4>
            <p className="text-teal-700 mb-4">
              Based on your destination and travel preferences, here's our
              recommended budget for this trip:
            </p>

            <div className="bg-white rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 text-center mb-3">
                Per Person Cost
              </p>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Minimum</p>
                  <p className="text-xl font-bold text-gray-900">
                    {budgetError.details.suggestedBudget.currency}{" "}
                    {budgetError.details.suggestedBudget.minimum?.toLocaleString()}
                  </p>
                </div>
                <div className="text-gray-300 text-2xl">→</div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Recommended</p>
                  <p className="text-2xl font-bold text-teal-600">
                    {budgetError.details.suggestedBudget.currency}{" "}
                    {budgetError.details.suggestedBudget.recommended?.toLocaleString()}
                  </p>
                </div>
              </div>
              {budgetError.details.suggestedBudget.totalTravelers > 1 && (
                <p className="text-xs text-gray-400 text-center mt-2">
                  Total for {budgetError.details.suggestedBudget.totalTravelers}{" "}
                  travelers: {budgetError.details.suggestedBudget.currency}{" "}
                  {budgetError.details.suggestedBudget.totalRecommended?.toLocaleString()}
                </p>
              )}
            </div>

            <p className="text-sm text-gray-600 text-center">
              Click "Continue" below to generate your personalized itinerary
            </p>
          </div>

          <button
            onClick={onProceedAnyway}
            className="w-full py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-colors cursor-pointer flex items-center justify-center gap-2"
          >
            Continue to Generate Itinerary
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-red-600 mb-4">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-semibold">Validation Issues Found</span>
        </div>

        {budgetError && (
          <div
            className={`${noBudgetProvided ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200"} border rounded-xl p-6`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4
                className={`font-semibold ${noBudgetProvided ? "text-teal-800" : "text-red-800"} flex items-center gap-2`}
              >
                <span>
                  {noBudgetProvided ? "Estimated Budget" : "Budget Too Low"}
                </span>
              </h4>
              {!noBudgetProvided && (
                <button
                  onClick={() => {
                    trackEvent("edit_budget_clicked");
                    onGoToStep(3);
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit Budget
                </button>
              )}
            </div>
            {!noBudgetProvided && (
              <p className="text-red-700 mb-4">{budgetError.message}</p>
            )}
            {noBudgetProvided && (
              <p className="text-teal-700 mb-4">
                Based on your destination and preferences, here's our
                recommended budget:
              </p>
            )}

            {budgetError.details?.suggestedBudget && (
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Suggested Budget Range:
                </p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Minimum</p>
                    <p className="text-lg font-bold text-gray-900">
                      {budgetError.details.suggestedBudget.currency}{" "}
                      {budgetError.details.suggestedBudget.minimum?.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-gray-300">-</div>
                  <div>
                    <p className="text-xs text-gray-500">Recommended</p>
                    <p className="text-lg font-bold text-teal-600">
                      {budgetError.details.suggestedBudget.currency}{" "}
                      {budgetError.details.suggestedBudget.recommended?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {budgetError.details?.missingExperiences?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Experiences you might miss:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {budgetError.details.missingExperiences
                    .slice(0, 4)
                    .map((exp, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-red-400">-</span>
                        {exp}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {budgetError.details?.alternatives?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Alternative Destinations:
                </p>
                <div className="space-y-2">
                  {budgetError.details.alternatives
                    .slice(0, 3)
                    .map((alt, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium text-gray-900">{alt.name}</p>
                        <p className="text-xs text-gray-500">{alt.reason}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {weatherError && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-amber-800">Weather Mismatch</h4>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    trackEvent("edit_dates_clicked");
                    onGoToStep(1);
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Edit Dates
                </button>
                <button
                  onClick={() => {
                    trackEvent("edit_weather_clicked");
                    onGoToStep(2);
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit Weather
                </button>
              </div>
            </div>
            <p className="text-amber-700 mb-4">
              You selected{" "}
              <strong>
                {weatherError.details?.userPreference?.join(", ")}
              </strong>{" "}
              weather, but {formData.destination} typically has{" "}
              <strong>
                {weatherError.details?.actualConditions?.join(" and ")}
              </strong>{" "}
              weather during your travel dates.
            </p>

            {weatherError.recommendedMonths?.length > 0 && (
              <p className="text-sm text-gray-600">
                For your preferred weather, consider traveling in:{" "}
                <strong>{weatherError.recommendedMonths.join(", ")}</strong>
              </p>
            )}
          </div>
        )}

        <div className="bg-gray-50 rounded-xl p-4 text-center space-y-3">
          <p className="text-gray-600 text-sm">
            Use the "Edit" buttons above to adjust your selections, or continue
            anyway.
          </p>
          <button
            onClick={onProceedAnyway}
            className="w-full py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-colors cursor-pointer flex items-center justify-center gap-2"
          >
            Continue Anyway
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 text-5xl mb-4">!</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Something went wrong
        </h3>
        <p className="text-gray-500 text-center max-w-md mb-4">{error}</p>
        {onRegenerate && (
          <button
            onClick={() => {
              trackEvent("itinerary_regenerated", {
                regeneration_source: "error",
              });
              onRegenerate();
            }}
            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-full transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (analysisComplete && itinerary) {
    const { tripSummary, dayWiseItinerary, destinationImages } = itinerary;
    const isStreaming = !!itinerary._streaming;
    const dayDates = itinerary._dayDates || [];
    const totalDays = dayDates.length || dayWiseItinerary?.length || 0;
    const daysByNum = new Map(
      (dayWiseItinerary || []).map((d) => [d.dayNumber, d]),
    );
    const streamMessage = itinerary._streamMessage;
    const streamLog = itinerary._streamLog || [];
    const citiesBreakdown = tripSummary?.citiesBreakdown;

    const slots =
      isStreaming && totalDays
        ? dayDates.map((dd) => ({
            dayNum: dd.dayNum,
            date: dd.formattedDate,
            day: daysByNum.get(dd.dayNum) || null,
          }))
        : (dayWiseItinerary || []).map((d, i) => ({
            dayNum: d.dayNumber || i + 1,
            date: d.date,
            day: d,
          }));

    return (
      <div className="space-y-6">
        {saveError && !saveBannerDismissed && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm">
            <span className="text-red-700">{saveError}</span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => {
                  trackEvent("retry_save_clicked");
                  onRetrySave();
                }}
                className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-full transition-colors"
              >
                Retry Save
              </button>
              <button
                onClick={() => setSaveBannerDismissed(true)}
                className="text-red-400 hover:text-red-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 text-teal-600 mb-4">
          <SparklesIcon />
          <span className="font-semibold">
            {isStreaming
              ? "Building your itinerary…"
              : "Your AI-Generated Itinerary"}
          </span>
        </div>

        {isStreaming && streamMessage && (
          <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-100 rounded-full text-sm text-teal-700 w-fit">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span>{streamMessage}</span>
          </div>
        )}

        {isStreaming && streamLog.length > 0 && (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
              Progress
            </p>
            <ul className="space-y-1.5 text-sm">
              {streamLog.map((msg, i) => {
                const isLatest = i === streamLog.length - 1;
                return (
                  <li key={i} className="flex items-start gap-2 stream-fade-in">
                    <span
                      className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${isLatest ? "bg-teal-500 animate-pulse" : "bg-gray-300"}`}
                    ></span>
                    <span
                      className={
                        isLatest ? "text-gray-900 font-medium" : "text-gray-500"
                      }
                    >
                      {msg}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!isStreaming && destinationImages && destinationImages.length > 0 && (
          <div className="grid grid-cols-4 gap-2 rounded-xl overflow-hidden">
            <div className="col-span-2 row-span-2">
              <img
                src={destinationImages[0].url}
                alt={tripSummary?.destination}
                className="w-full h-full object-cover"
              />
            </div>
            {destinationImages.slice(1, 5).map((img, idx) => (
              <div key={idx} className="aspect-[4/3]">
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        <div className="bg-teal-50 rounded-xl p-6">
          {tripSummary?.tripTitle ? (
            <h3
              className="text-xl font-bold text-gray-900 mb-2 stream-fade-in"
              key={tripSummary.tripTitle}
            >
              {tripSummary.tripTitle}
            </h3>
          ) : (
            <div className="h-6 w-2/3 bg-teal-100 rounded animate-pulse mb-2"></div>
          )}
          {tripSummary?.overview ? (
            <p
              className="text-gray-700 text-sm mb-4 stream-fade-in"
              key={tripSummary.overview}
            >
              {tripSummary.overview}
            </p>
          ) : isStreaming ? (
            <div className="space-y-2 mb-4">
              <div className="h-3 w-full bg-teal-100 rounded animate-pulse"></div>
              <div className="h-3 w-5/6 bg-teal-100 rounded animate-pulse"></div>
            </div>
          ) : null}

          {citiesBreakdown && citiesBreakdown.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4 stream-fade-in">
              {citiesBreakdown.map((c, i) => (
                <span
                  key={i}
                  className={`px-3 py-1 rounded-full text-xs border ${
                    c.type === "departure"
                      ? "bg-white border-sky-200 text-sky-600"
                      : "bg-white border-teal-200 text-teal-700"
                  }`}
                >
                  {c.type === "departure" ? "✈ " : ""}
                  {c.city}
                  {c.nights > 0 ? ` · ${c.nights}n` : ""}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Duration</span>
              <p className="font-medium text-gray-900">
                {tripSummary?.duration}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Travelers</span>
              <p className="font-medium text-gray-900">
                {tripSummary?.travelers?.description}
              </p>
            </div>
          </div>
        </div>

        {slots.length > 0 && (
          <div className="border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4">
              {isStreaming
                ? `Itinerary (${(dayWiseItinerary || []).length} of ${totalDays} days ready)`
                : `Complete Itinerary (${slots.length} Days)`}
            </h4>
            <div className="space-y-3">
              {slots.map((slot) => (
                <div
                  key={slot.dayNum}
                  className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-700 font-bold text-sm shrink-0">
                    Day {slot.dayNum}
                  </div>
                  <div className="flex-1 min-w-0">
                    {slot.day ? (
                      <div className="stream-fade-in">
                        <p className="font-medium text-gray-900">
                          {slot.day.title}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(slot.day.date || slot.date)}
                        </p>
                        {slot.day.highlights && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {slot.day.highlights.slice(0, 4).map((h, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center text-xs text-gray-600"
                              >
                                <span className="w-1 h-1 bg-teal-500 rounded-full mr-1"></span>
                                {h}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-400">
                            {slot.date || ""}
                          </p>
                          <span className="inline-flex items-center gap-1 text-[11px] text-teal-600">
                            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
                            Generating…
                          </span>
                        </div>
                        <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse mt-2"></div>
                        <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse mt-2"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isStreaming && (
          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
            <p className="text-teal-900 text-sm leading-relaxed">
              <span className="font-semibold">Want to tweak something?</span>{" "}
              Use the
              <span className="font-semibold"> Edit Itinerary</span> chat on the
              left to add or remove activities, swap restaurants, change a day's
              theme, extend or shorten the trip, or share any special requests.
              When you're ready, click{" "}
              <span className="font-semibold"> View Full Itinerary</span> below
              to see the full day-by-day plan with hotels, costs, and booking
              links.
            </p>
          </div>
        )}

        {!isStreaming && isEditMode && (
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-blue-800 text-sm mb-3">
              <strong>Edit Mode:</strong> You can go back to modify your
              preferences, then regenerate your itinerary.
            </p>
            <button
              onClick={() => {
                trackEvent("itinerary_regenerated", {
                  regeneration_source: "edit_mode",
                });
                onRegenerate();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors cursor-pointer"
            >
              Regenerate Itinerary
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default Step4Form;
